// Angular
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// Material
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
// RxJS
import { Subscription } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
// Services and Models
import {
  PatientActionTypes,
  PatientModel,
  PatientOnServerCreated,
  PatientsService,
  selectPatientLatestSuccessfullAction,
} from '../../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { skip } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-patient-add',
  templateUrl: './patient-add.dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatePatientDialogComponent implements OnInit, OnDestroy {
  patientForm: FormGroup;
  hasFormErrors: boolean;
  clicked: boolean;
  companyId: number;
  filteredFacilities: any[];
  private subscriptions: Subscription[] = [];
  facilityAutocompleteCall = this.patientsService.searchFacilities.bind(this.patientsService);
  @ViewChild('facilityAutocomplete') facilityAutocomplete: GeneralAutocompleteComponent;
  @Input() patient;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CreatePatientDialogComponent>,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private translate: TranslateService,
    public dialog: MatDialog,
    private patientsService: PatientsService,
    private datePipe: DatePipe,
    public stringUtils: StringUtilsService,
  ) {}

  ngOnInit() {
    this.createForm();
    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      this.companyId = res !== undefined ? res.companyID : -1;
    });
    this.subscriptions.push(userSub);
    const successSubscription = this.store
      .pipe(select(selectPatientLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          switch (res.action_type) {
            case PatientActionTypes.PatientCreated:
              this.dialogRef.close({ added: true });
              break;
            default:
              break;
          }
        }
      });
    this.subscriptions.push(successSubscription);
  }

  getTitle() {
    return this.translate.instant('PATIENT.NEW_PATIENT');
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Save data
   */
  onSubmit() {
    this.hasFormErrors = false;
    const controls = this.patientForm.controls;
    /** check form */
    this.facilityAutocomplete.updateValueAndValidity();

    if (this.patientForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      return;
    }
    this.clicked = true;
    this.createPatient(this.preparePatient());
  }

  /**
   * Returns object for saving
   */
  preparePatient(): PatientModel {
    const controls = this.patientForm.controls;
    const _patient = new PatientModel();
    _patient.first_name = controls.first_name.value;
    _patient.middle_name = controls.middle_name.value;
    _patient.last_name = controls.last_name.value;
    _patient.last_name2 = controls.last_name2.value;
    _patient.dob = this.datePipe.transform(controls.dob.value, 'yyyy-MM-dd');
    _patient.gender = controls.gender.value;
    _patient.primary_language = controls.primary_language.value;
    _patient.patient_class = controls.patient_class.value;
    _patient.facility_id = controls.facility_id.value ? controls.facility_id.value.id : null;
    _patient.urbanization = controls.urbanization.value;
    _patient.address_1 = controls.address_1.value;
    _patient.address_2 = controls.address_2.value;
    _patient.city = controls.city.value;
    _patient.state = controls.state.value ? controls.state.value.toUpperCase() : '';
    _patient.zip = controls.zip.value;
    _patient.home_phone =
      controls.home_phone.value !== null
        ? controls.home_phone.value.replace(/\D/g, '')
        : controls.home_phone.value;
    _patient.cell_phone =
      controls.cell_phone.value !== null
        ? controls.cell_phone.value.replace(/\D/g, '')
        : controls.cell_phone.value;
    _patient.fax =
      controls.fax.value !== null ? controls.fax.value.replace(/\D/g, '') : controls.fax.value;
    _patient.email = controls.email.value;
    _patient.hold_flag = controls.hold_flag.value;
    _patient.hold_date = this.datePipe.transform(controls.hold_date.value, 'yyyy-MM-dd hh:mm:ss');
    _patient.hold_note = controls.hold_note.value;
    _patient.in_hospital = controls.in_hospital.value;
    _patient.in_hospital_date = this.datePipe.transform(
      controls.in_hospital_date.value,
      'yyyy-MM-dd hh:mm:ss',
    );
    _patient.is_active = controls.is_active.value;
    _patient.patient_id = '';
    _patient.processed = true;
    for (const [key, value] of Object.entries(_patient)) {
      if (typeof value === 'string') {
        _patient[key] = value.trim();
      }
    }
    return _patient;
  }

  /**
   * Create patient
   *
   * @param _patient: Patient
   */
  createPatient(_patient: PatientModel) {
    this.store.dispatch(new PatientOnServerCreated({ patient: _patient }));
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.patientForm.controls[controlName];
    if (!control) {
      return false;
    }
    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  close() {
    this.dialogRef.close();
  }

  createForm() {
    this.patientForm = this.fb.group({
      first_name: [this.patient ? this.patient.first_name : null, Validators.required],
      last_name: [this.patient ? this.patient.last_name : null, Validators.required],
      middle_name: [this.patient ? this.patient.middle_name : null],
      last_name2: [this.patient ? this.patient.last_name2 : null],
      dob: [this.patient ? this.patient.dob : null, Validators.required],
      gender: [this.patient ? this.patient.gender : null, Validators.required],
      primary_language: [this.patient ? this.patient.primary_language : null],
      patient_class: [this.patient ? this.patient.patient_class : null],
      facility_id: [this.patient ? this.patient.facility_id : null],
      urbanization: [this.patient ? this.patient.location.urbanization : null],
      address_1: [this.patient ? this.patient.location.address_1 : null],
      address_2: [this.patient ? this.patient.location.address_2 : null],
      city: [this.patient ? this.patient.location.city : null],
      state: [
        this.patient ? this.patient.location.state : null,
        Validators.pattern(/^[A-Za-z]{2}$/),
      ],
      zip: [
        this.patient ? this.patient.location.zip : null,
        Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/),
      ],
      home_phone: [
        this.patient ? this.patient.home_phone : null,
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      cell_phone: [
        this.patient ? this.patient.cell_phone : null,
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      fax: [
        this.patient ? this.patient.fax : null,
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      email: [this.patient ? this.patient.email : null, Validators.email],
      hold_flag: [this.patient ? this.patient.email : null],
      hold_date: [
        this.patient ? (this.patient.hold_date ? new Date(this.patient.hold_date) : null) : null,
      ],
      hold_note: [this.patient ? this.patient.hold_note : null],
      in_hospital: [this.patient ? this.patient.in_hospital : null],
      in_hospital_date: [
        this.patient
          ? this.patient.in_hospital_date
            ? new Date(this.patient.in_hospital_date)
            : null
          : null,
      ],
      is_active: [this.patient ? this.patient.is_active : true],
    });
    if (this.patient) {
      if (this.patient.facility_id && this.patient.facility_id !== 0) {
        const facilitySub = this.patientsService
          .getFacility(this.patient.facility_id)
          .subscribe((result) => {
            if (result.data) {
              setTimeout(() => {
                this.facilityAutocomplete.setValue({
                  id: this.patient.facility_id,
                  last_name: result.data.name,
                });
              });
            }
          });
        this.subscriptions.push(facilitySub);
      }
      this.patientForm.disable();
    }
  }
  onFacilityChange(val) {
    this.patientForm.controls.facility_id.setValue(val.id);
    this.patientForm.controls.facility_id.updateValueAndValidity();
  }

  getPatientName(patient) {
    return patient.first_name + ' ' + patient.last_name;
  }

  getPatientAvatarUrl(patient) {
    return patient.pre_signed_url && patient.pre_signed_url.url ? patient.pre_signed_url.url : '';
  }
}
